import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

// ---------- Section renderers ----------
const SectionHeader = ({ eyebrow, heading }) => (
  <div className="mb-10 md:mb-14 max-w-3xl">
    {eyebrow && (
      <div className="flex items-center gap-3 mb-5">
        <div className="h-px w-8 bg-[#FF5F1F]" />
        <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="font-headings text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] text-white">
      {heading}
    </h2>
  </div>
);

const Wrap = ({ children, dark = false, testid }) => (
  <section
    data-testid={testid}
    className={`relative border-t border-white/10 py-20 md:py-28 ${dark ? "bg-[#0A0A0A]" : "bg-[#050505]"}`}
  >
    <div className="max-w-7xl mx-auto px-4 md:px-6">{children}</div>
  </section>
);

const ProseSection = ({ section, i }) => (
  <Wrap dark={i % 2 === 1} testid={`section-${i}`}>
    <SectionHeader eyebrow={section.eyebrow} heading={section.heading} />
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="max-w-3xl text-base md:text-lg text-zinc-400 leading-relaxed"
    >
      {section.body}
    </motion.p>
  </Wrap>
);

const ListSection = ({ section, i }) => (
  <Wrap dark={i % 2 === 1} testid={`section-${i}`}>
    <SectionHeader eyebrow={section.eyebrow} heading={section.heading} />
    {section.note && <p className="text-zinc-400 mb-8 max-w-2xl">{section.note}</p>}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
      {section.items.map((item, idx) => (
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.4, delay: (idx % 6) * 0.05 }}
          className="bg-[#0A0A0A] hover:bg-[#111] transition-colors p-6 flex items-start gap-3"
        >
          <Check className="w-4 h-4 text-[#FF5F1F] mt-1 shrink-0" strokeWidth={2.5} />
          <span className="text-sm md:text-base text-zinc-200 leading-relaxed">{item}</span>
        </motion.div>
      ))}
    </div>
    {section.footnote && (
      <p className="mt-8 text-sm text-zinc-500 italic max-w-2xl">{section.footnote}</p>
    )}
  </Wrap>
);

const CapabilitiesSection = ({ section, i }) => (
  <Wrap dark={i % 2 === 1} testid={`section-${i}`}>
    <SectionHeader eyebrow={section.eyebrow} heading={section.heading} />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
      {section.items.map(([label, desc], idx) => (
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.5, delay: (idx % 4) * 0.06 }}
          className="bg-[#0A0A0A] hover:bg-[#111] transition-colors p-6 md:p-8 flex flex-col gap-3 group"
        >
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 group-hover:text-[#FF5F1F] transition-colors">
              /{String(idx + 1).padStart(2, "0")}
            </span>
            <div className="h-px flex-1 ml-4 bg-white/10 group-hover:bg-[#FF5F1F]/40 transition-colors" />
          </div>
          <h3 className="font-headings text-xl md:text-2xl font-bold text-white tracking-tight leading-tight">
            {label}
          </h3>
          <p className="text-sm md:text-base text-zinc-400 leading-relaxed">{desc}</p>
        </motion.div>
      ))}
    </div>
  </Wrap>
);

const StepsSection = ({ section, i }) => (
  <Wrap dark={i % 2 === 1} testid={`section-${i}`}>
    <SectionHeader eyebrow={section.eyebrow} heading={section.heading} />
    <div className="flex flex-col">
      {section.items.map(([label, desc], idx) => (
        <motion.div
          key={idx}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, delay: idx * 0.05 }}
          className="group grid grid-cols-[auto_1fr] md:grid-cols-[100px_180px_1fr] gap-6 md:gap-10 border-t border-white/10 py-8 md:py-10 last:border-b hover:bg-white/[0.015] transition-colors"
        >
          <div className="font-mono text-xs md:text-sm uppercase tracking-[0.2em] text-[#FF5F1F]">
            {String(idx + 1).padStart(2, "0")}
          </div>
          <h3 className="font-headings text-xl md:text-3xl font-bold text-white tracking-tight leading-tight md:col-auto col-span-2">
            {label}
          </h3>
          <p className="text-sm md:text-base text-zinc-400 leading-relaxed md:col-auto col-span-2 max-w-2xl">
            {desc}
          </p>
        </motion.div>
      ))}
    </div>
  </Wrap>
);

export const RenderSection = ({ section, i }) => {
  switch (section.type) {
    case "prose":
      return <ProseSection section={section} i={i} />;
    case "list":
      return <ListSection section={section} i={i} />;
    case "capabilities":
      return <CapabilitiesSection section={section} i={i} />;
    case "steps":
      return <StepsSection section={section} i={i} />;
    default:
      return null;
  }
};
